/**
 * Component Loader Module
 *
 * Dynamically loads HTML components following OOA/OOD principles:
 * - Factory Pattern: Creates component instances on demand
 * - Singleton Pattern: Single loader instance with shared cache
 * - Lazy Loading: Components loaded only when needed
 *
 * @module ComponentLoader
 */

// Component types for type safety
export type ComponentType = 'layout' | 'page' | 'modal';

export interface ComponentConfig {
    type: ComponentType;
    name: string;
    containerId: string;
}

/**
 * ComponentLoader - Manages dynamic HTML component loading
 */
export class ComponentLoader {
    private static instance: ComponentLoader;
    private cache: Map<string, string> = new Map();
    private loadingPromises: Map<string, Promise<string>> = new Map();
    private basePath: string = 'components';

    private constructor() {}

    /**
     * Get singleton instance
     */
    public static getInstance(): ComponentLoader {
        if (!ComponentLoader.instance) {
            ComponentLoader.instance = new ComponentLoader();
        }
        return ComponentLoader.instance;
    }

    /**
     * Build component path from type and name
     */
    private buildPath(type: ComponentType, name: string): string {
        const typeFolder = type === 'page' ? 'pages' : type === 'modal' ? 'modals' : 'layout';
        return `${this.basePath}/${typeFolder}/${name}.html`;
    }

    /**
     * Fetch component HTML from server
     */
    private async fetchComponent(path: string): Promise<string> {
        // Check cache first
        if (this.cache.has(path)) {
            return this.cache.get(path)!;
        }

        // Check if already loading
        if (this.loadingPromises.has(path)) {
            return this.loadingPromises.get(path)!;
        }

        // Fetch component
        const loadPromise = fetch(path)
            .then(response => {
                if (!response.ok) {
                    throw new Error(`Failed to load component: ${path} (${response.status})`);
                }
                return response.text();
            })
            .then(html => {
                this.cache.set(path, html);
                this.loadingPromises.delete(path);
                return html;
            })
            .catch(error => {
                this.loadingPromises.delete(path);
                console.error(`ComponentLoader: Failed to load ${path}`, error);
                throw error;
            });

        this.loadingPromises.set(path, loadPromise);
        return loadPromise;
    }

    /**
     * Load a component and inject into container
     */
    public async load(config: ComponentConfig): Promise<void> {
        const { type, name, containerId } = config;
        const path = this.buildPath(type, name);

        try {
            const html = await this.fetchComponent(path);
            const container = document.getElementById(containerId);

            if (!container) {
                console.error(`ComponentLoader: Container not found: ${containerId}`);
                return;
            }

            container.innerHTML = html;
            console.log(`ComponentLoader: Loaded ${type}/${name} into #${containerId}`);

            // Dispatch event for component initialization
            const event = new CustomEvent('componentLoaded', {
                detail: { type, name, containerId }
            });
            document.dispatchEvent(event);

        } catch (error) {
            console.error(`ComponentLoader: Error loading ${type}/${name}`, error);
        }
    }

    /**
     * Load multiple components in parallel
     */
    public async loadAll(configs: ComponentConfig[]): Promise<void> {
        await Promise.all(configs.map(config => this.load(config)));
    }

    /**
     * Load a page component
     */
    public async loadPage(name: string, containerId: string = 'page-content'): Promise<void> {
        await this.load({ type: 'page', name, containerId });
    }

    /**
     * Load a modal component
     */
    public async loadModal(name: string, containerId: string = 'modal-container'): Promise<void> {
        await this.load({ type: 'modal', name, containerId });
    }

    /**
     * Load a layout component
     */
    public async loadLayout(name: string, containerId: string): Promise<void> {
        await this.load({ type: 'layout', name, containerId });
    }

    /**
     * Preload components for faster subsequent access
     */
    public async preload(configs: ComponentConfig[]): Promise<void> {
        const paths = configs.map(c => this.buildPath(c.type, c.name));
        await Promise.all(paths.map(path => this.fetchComponent(path)));
        console.log(`ComponentLoader: Preloaded ${paths.length} components`);
    }

    /**
     * Clear the component cache
     */
    public clearCache(): void {
        this.cache.clear();
        console.log('ComponentLoader: Cache cleared');
    }

    /**
     * Check if a component is cached
     */
    public isCached(type: ComponentType, name: string): boolean {
        const path = this.buildPath(type, name);
        return this.cache.has(path);
    }
}

// Export singleton instance
export const componentLoader = ComponentLoader.getInstance();

// Expose globally for debugging
(window as any).componentLoader = componentLoader;
